import { Controller } from "@hotwired/stimulus";

import { AirDatepicker, localeRu } from "../lib/datepicker.js";
import { post } from "../lib/http.js";
import { showToast } from "../lib/toast.js";

/** Общие настройки обоих календарей формы. */
const DATEPICKER = {
  autoClose: true,
  timepicker: true,
  timeFormat: "HH:mm",
  dateFormat: "dd.MM.yyyy",
  locale: localeRu,
};

/**
 * Создание совместного чтения.
 *
 * Карточку выбранной книги отдаёт сервер: в каждой строке поиска лежит
 * <template> с готовой разметкой, контроллер её клонирует. Раньше карточка
 * склеивалась строкой в JS, а данные для неё выскребались из DOM строки
 * поиска по классам.
 */
export default class extends Controller {
  static targets = [
    "modal", "form", "search", "dropdown", "results", "preview",
    "pages", "startDate", "endDate", "startLabel", "endLabel",
  ];
  static values = {
    searchUrl: String,
    createUrl: String,
    minLength: { type: Number, default: 3 },
    delay: { type: Number, default: 300 },
    defaultParticipants: { type: Number, default: 25 },
  };

  connect() {
    this.bookId = null;
    // Календарь рендерим внутрь <dialog>: модалка лежит в top layer браузера,
    // и выпадашка из <body> оказалась бы под ней — даты не нажимались.
    const container = this.modalTarget;
    this.startPicker = new AirDatepicker(this.startDateTarget, {
      ...DATEPICKER,
      container,
      minDate: new Date(),
      onSelect: ({ formattedDate }) => {
        this.startLabelTarget.textContent = formattedDate;
      },
    });
    this.endPicker = new AirDatepicker(this.endDateTarget, {
      ...DATEPICKER,
      container,
      minDate: new Date(),
      onSelect: ({ formattedDate }) => {
        this.endLabelTarget.textContent = formattedDate;
      },
    });
  }

  disconnect() {
    clearTimeout(this.timer);
    this.startPicker?.destroy();
    this.endPicker?.destroy();
  }

  open() {
    this.modalTarget.showModal();
  }

  close() {
    this.modalTarget.close();
    this.reset();
  }

  showStartPicker() {
    this.startPicker.show();
  }

  showEndPicker() {
    this.endPicker.show();
  }

  /** Поиск книги с паузой после набора. */
  search() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fetchBooks(), this.delayValue);
  }

  async fetchBooks() {
    const query = this.searchTarget.value.trim();
    if (query.length < this.minLengthValue) {
      this.dropdownTarget.hidden = true;
      return;
    }

    const response = await fetch(
      `${this.searchUrlValue}?query=${encodeURIComponent(query)}`,
      { headers: { Accept: "text/html" } },
    );
    if (!response.ok) return;

    this.resultsTarget.innerHTML = await response.text();
    this.dropdownTarget.hidden = !this.resultsTarget.textContent.trim();
  }

  /** Выбор книги: переносим готовую карточку из <template> строки. */
  selectBook(event) {
    const item = event.currentTarget;
    this.bookId = item.dataset.bookId;

    if (item.dataset.bookPages) this.pagesTarget.value = item.dataset.bookPages;
    this.searchTarget.value = item.dataset.bookName;

    this.previewTarget.replaceChildren(
      item.querySelector("[data-book-preview]").content.cloneNode(true),
    );
    this.dropdownTarget.hidden = true;
  }

  /** Отправляет форму и уводит на страницу созданного чтения. */
  async submit(event) {
    event.preventDefault();

    if (!this.bookId) {
      showToast("Выберите книгу");
      return;
    }
    if (!this.startPicker.lastSelectedDate || !this.endPicker.lastSelectedDate) {
      showToast("Укажите даты начала и окончания");
      return;
    }

    const form = new FormData(this.formTarget);
    form.set("book", this.bookId);
    form.set("start_date", this.startPicker.lastSelectedDate.toISOString());
    form.set("end_date", this.endPicker.lastSelectedDate.toISOString());
    form.set("max_participants", String(this.maxParticipants(form)));
    form.delete("limit");

    try {
      const reading = await post(this.createUrlValue, form);
      window.location.href = `/readings/${reading.pk}/`;
    } catch (error) {
      showToast(error.message);
    }
  }

  /**
   * Предел участников: введённое число важнее выбранной кнопки,
   * «∞» означает практический потолок.
   */
  maxParticipants(form) {
    const typed = Number(form.get("max_participants"));
    if (typed) return typed;

    const preset = form.get("limit");
    if (!preset) return this.defaultParticipantsValue;
    return preset === "infinity" ? 999 : Number(preset);
  }

  reset() {
    this.bookId = null;
    this.formTarget.reset();
    this.previewTarget.replaceChildren();
    this.resultsTarget.replaceChildren();
    this.dropdownTarget.hidden = true;
    this.startLabelTarget.textContent = "Дата начала";
    this.endLabelTarget.textContent = "Дата окончания";
    this.startPicker.clear();
    this.endPicker.clear();
  }
}
